import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { geoContains, geoEquirectangular, geoGraticule, geoPath } from "d3-geo";
import { feature, merge, mesh } from "topojson-client";
import type { GeometryCollection, MultiPolygon as TopoMultiPolygon, Polygon as TopoPolygon, Topology } from "topojson-specification";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import { colorForHour } from "../color";
import { db, cityName, zoneById } from "../tz";
import { formatDate, formatOffset, formatTime, fromCivil, toCivil, type TimeFormat } from "../tz/civil";

const W = 960;
const H = 480;
const projection = geoEquirectangular().fitSize([W, H], { type: "Sphere" });
const pathOf = geoPath(projection);
const graticuleD = pathOf(geoGraticule().step([15, 15])()) ?? "";

type ZoneGeometry = TopoPolygon<{ tzid: string }> | TopoMultiPolygon<{ tzid: string }>;
type ZoneFeature = Feature<Polygon | MultiPolygon, { tzid: string }>;

interface MapData {
  topo: Topology;
  geometries: Map<string, ZoneGeometry>;
  features: Map<string, ZoneFeature>;
  oceanD: string; // union of the ocean (Etc/*) zones
  landBordersD: string; // boundaries between land zones
  coastD: string; // land/sea boundary
}

const isLand = (g: { properties?: unknown }) => !/^Etc\//.test((g.properties as { tzid: string }).tzid);

interface Region {
  key: string;
  members: string[]; // canonical zone ids, map polygons
  d: string;
}

interface Props {
  instant: number;
  sourceDay: string; // ISO date in the source zone, for +1/-1 day labels
  selected: string;
  fmt: TimeFormat;
  changeWindow: number; // seconds around the instant to flag upcoming/recent changes
  watched: string[];
  onToggle: (id: string) => void;
}

let cached: Promise<MapData> | null = null;
function loadMap(): Promise<MapData> {
  if (!cached) {
    cached = fetch(`${import.meta.env.BASE_URL}data/timezones.topojson`)
      .then((r) => r.json())
      .then((topo: Topology) => {
        const coll = topo.objects.zones as GeometryCollection<{ tzid: string }>;
        const geometries = new Map<string, ZoneGeometry>();
        const features = new Map<string, ZoneFeature>();
        for (const g of coll.geometries) {
          if (g.type !== "Polygon" && g.type !== "MultiPolygon") continue;
          const id = (g.properties as { tzid: string }).tzid;
          if (!db.has(id)) continue;
          geometries.set(id, g);
          features.set(id, feature(topo, g) as ZoneFeature);
        }
        const ocean = coll.geometries.filter((g) => !isLand(g)) as ZoneGeometry[];
        const oceanD = pathOf(merge(topo, ocean)) ?? "";
        const landBordersD = pathOf(mesh(topo, coll, (a, b) => a !== b && isLand(a) && isLand(b))) ?? "";
        const coastD = pathOf(mesh(topo, coll, (a, b) => (a === b ? isLand(a) : isLand(a) !== isLand(b)))) ?? "";
        return { topo, geometries, features, oceanD, landBordersD, coastD };
      });
  }
  return cached;
}

/**
 * Zones that keep the same UTC offset as each other throughout the window
 * (the calendar year of the instant plus a year either side) are drawn as one
 * region. Abbreviations are ignored: London and Lisbon read the same clock.
 */
function behaviourKey(id: string, from: number, to: number): string {
  const z = db.zone(id);
  const parts = [String(z.offsetAt(from))];
  for (const tr of z.transitionsBetween(from, to)) {
    if (tr.after.utoff !== tr.before.utoff) parts.push(`${tr.at}:${tr.after.utoff}`);
  }
  return parts.join(",");
}

export function WorldMap({ instant, sourceDay, selected, fmt, changeWindow, watched, onToggle }: Props) {
  const [data, setData] = useState<MapData | null>(null);
  const [hover, setHover] = useState<{ key: string; id: string; x: number; y: number } | null>(null);
  const [view, setView] = useState({ x: 0, y: 0, w: W, h: H });
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean; view: typeof view; id: string | null } | null>(null);

  useEffect(() => {
    loadMap().then(setData);
  }, []);

  // Non-passive wheel handler so we can prevent page scroll while zooming.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      setView((v) => {
        const factor = Math.exp(e.deltaY * 0.002);
        const w = Math.min(W, Math.max(W / 16, v.w * factor));
        const h = (w * H) / W;
        const x = Math.min(W - w, Math.max(0, v.x + (v.w - w) * px));
        const y = Math.min(H - h, Math.max(0, v.y + (v.h - h) * py));
        return { x, y, w, h };
      });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, []);

  // End a drag even when the pointer is released outside the map.
  useEffect(() => {
    const up = () => {
      drag.current = null;
    };
    addEventListener("pointerup", up);
    return () => removeEventListener("pointerup", up);
  }, []);

  const year = toCivil(instant).year;
  const regions = useMemo<Region[]>(() => {
    if (!data) return [];
    const from = fromCivil(year - 1, 1, 1);
    const to = fromCivil(year + 2, 1, 1);
    const groups = new Map<string, string[]>();
    for (const id of data.geometries.keys()) {
      const key = behaviourKey(id, from, to);
      let g = groups.get(key);
      if (!g) groups.set(key, (g = []));
      g.push(id);
    }
    const out: Region[] = [];
    for (const [key, members] of groups) {
      members.sort((a, b) => (zoneById.has(a) === zoneById.has(b) ? cityName(a).localeCompare(cityName(b)) : zoneById.has(a) ? -1 : 1));
      const geom = members.length === 1 ? data.features.get(members[0])!.geometry : merge(data.topo, members.map((m) => data.geometries.get(m)!));
      const d = pathOf(geom);
      if (d) out.push({ key, members, d });
    }
    return out;
  }, [data, year]);

  const regionOf = useMemo(() => {
    const m = new Map<string, Region>();
    for (const r of regions) for (const id of r.members) m.set(id, r);
    return m;
  }, [regions]);

  const info = useMemo(() => {
    const m = new Map<string, { hour: number; changing: boolean }>();
    for (const r of regions) {
      const z = db.zone(r.members[0]);
      const t = z.typeAt(instant);
      const local = toCivil(instant + t.utoff);
      const changing = z.transitionsBetween(instant - changeWindow, instant + changeWindow).some((tr) => tr.after.utoff !== tr.before.utoff);
      m.set(r.key, { hour: local.hour + local.minute / 60, changing });
    }
    return m;
  }, [regions, instant, changeWindow]);

  const selectedRegion = regionOf.get(db.canonical(selected) ?? "");

  /** The member zone under a client point, falling back to the region's first member. */
  const zoneAt = (region: Region, clientX: number, clientY: number): string => {
    const svg = svgRef.current;
    if (!svg || !data || region.members.length === 1) return region.members[0];
    const ctm = svg.getScreenCTM();
    if (!ctm) return region.members[0];
    const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    const lonlat = projection.invert?.([p.x, p.y]);
    if (!lonlat) return region.members[0];
    for (const id of region.members) {
      const f = data.features.get(id);
      if (f && geoContains(f, lonlat)) return id;
    }
    return region.members[0];
  };

  const regionFromEvent = (e: Event): Region | null => {
    const key = (e.target as Element).getAttribute?.("data-region");
    return key ? (regions.find((r) => r.key === key) ?? null) : null;
  };

  const tooltip = useMemo(() => {
    if (!hover) return null;
    const region = regions.find((r) => r.key === hover.key);
    if (!region) return null;
    const z = db.zone(hover.id);
    const t = z.typeAt(instant);
    const local = toCivil(instant + t.utoff);
    const day = `${local.year}-${String(local.month).padStart(2, "0")}-${String(local.day).padStart(2, "0")}`;
    const dayDiff = day === sourceDay ? "" : day > sourceDay ? " (+1 day)" : " (−1 day)";
    const next = z.nextTransition(instant);
    const prev = z.prevTransition(instant, 86400 * 30);
    const others = region.members.filter((m) => m !== hover.id && zoneById.has(m)).map(cityName);
    return { z, t, local, dayDiff, next, prev, others };
  }, [hover, regions, instant, sourceDay]);

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const region = regionFromEvent(e);
    drag.current = { x: e.clientX, y: e.clientY, moved: false, view, id: region ? zoneAt(region, e.clientX, e.clientY) : null };
  };
  const onPointerMove = (e: PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    if (drag.current) {
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) drag.current.moved = true;
      if (drag.current.moved) {
        const rect = svg.getBoundingClientRect();
        const v = drag.current.view;
        const x = Math.min(W - v.w, Math.max(0, v.x - (dx / rect.width) * v.w));
        const y = Math.min(H - v.h, Math.max(0, v.y - (dy / rect.height) * v.h));
        setView({ ...v, x, y });
      }
    }
    const region = regionFromEvent(e);
    const rect = svg.getBoundingClientRect();
    if (region) setHover({ key: region.key, id: zoneAt(region, e.clientX, e.clientY), x: e.clientX - rect.left, y: e.clientY - rect.top });
    else setHover(null);
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.moved) return;
    if (d.id) onToggle(d.id);
  };

  const watchedPaths = useMemo(() => {
    if (!data) return [];
    return watched.map((id) => ({ id, d: data.features.has(id) ? (pathOf(data.features.get(id)!) ?? "") : "" })).filter((w) => w.d);
  }, [data, watched]);

  const width = svgRef.current?.clientWidth ?? 0;

  return (
    <div class="map-wrap">
      <svg
        ref={svgRef}
        class="map"
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => {
          setHover(null);
          drag.current = null;
        }}
        onDblClick={() => setView({ x: 0, y: 0, w: W, h: H })}
      >
        <defs>
          <pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.55)" stroke-width="1.5" />
          </pattern>
        </defs>
        <rect x={0} y={0} width={W} height={H} class="map-sea" />
        {regions.length > 0 && (
          <g>
            {regions.map((r) => {
              const i = info.get(r.key);
              return (
                <path
                  key={r.key}
                  data-region={r.key}
                  d={r.d}
                  class={`zone${hover?.key === r.key ? " hover" : ""}`}
                  fill={i ? colorForHour(i.hour) : "#555"}
                />
              );
            })}
            {regions
              .filter((r) => info.get(r.key)?.changing)
              .map((r) => (
                <path key={`h${r.key}`} data-region={r.key} d={r.d} fill="url(#hatch)" class="zone-hatch" />
              ))}
            {selectedRegion && <path d={selectedRegion.d} class="zone-outline" />}
            {watchedPaths.map((w) => (
              <path key={`w${w.id}`} d={w.d} class="zone-watched" />
            ))}
          </g>
        )}
        {data && (
          <g class="map-overlay">
            <path d={data.oceanD} class="ocean-shade" />
            <path d={data.landBordersD} class="land-borders" />
            <path d={data.coastD} class="coast" />
          </g>
        )}
        <path d={graticuleD} class="graticule" />
      </svg>
      {!data && <div class="map-loading">Loading map…</div>}
      {hover && tooltip && (
        <div
          class="map-tooltip"
          style={hover.x > width * 0.6 ? { right: `${width - hover.x + 14}px`, top: `${hover.y + 14}px` } : { left: `${hover.x + 14}px`, top: `${hover.y + 14}px` }}
        >
          <div class="tt-title">{cityName(tooltip.z.id)} <span class="muted">{tooltip.z.id}</span></div>
          <div class="tt-time">{formatTime(tooltip.local, fmt)} <span class="muted">{formatDate(tooltip.local)}{tooltip.dayDiff}</span></div>
          <div class="muted">
            {tooltip.t.abbr} · UTC{formatOffset(tooltip.t.utoff)}{tooltip.t.isdst ? " · daylight time" : ""}
          </div>
          {tooltip.next && (
            <div class="muted">
              Next change: {formatDate(toCivil(tooltip.next.at + tooltip.next.before.utoff))} {formatTime(toCivil(tooltip.next.at + tooltip.next.before.utoff), fmt)} → {formatTime(toCivil(tooltip.next.at + tooltip.next.after.utoff), fmt)} ({tooltip.next.after.abbr})
            </div>
          )}
          {tooltip.prev && (
            <div class="muted">
              Changed: {formatDate(toCivil(tooltip.prev.at + tooltip.prev.before.utoff))} ({tooltip.prev.before.abbr} → {tooltip.prev.after.abbr})
            </div>
          )}
          <div class="muted tt-hint">{watched.includes(tooltip.z.id) ? "Click to remove from the list" : "Click to add to the list"}</div>
          {tooltip.others.length > 0 && (
            <div class="muted tt-others">
              Same clock: {tooltip.others.slice(0, 8).join(", ")}{tooltip.others.length > 8 ? ` +${tooltip.others.length - 8}` : ""}
            </div>
          )}
        </div>
      )}
      <div class="map-legend" aria-hidden="true">
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} style={{ background: colorForHour(h + 0.5) }} title={`${h}:00`} />
        ))}
        <div class="map-legend-labels">
          <span>midnight</span><span>6 am</span><span>noon</span><span>6 pm</span><span>midnight</span>
        </div>
      </div>
    </div>
  );
}
